/** Fixed puzzle rules. Tuning arrives as recorded setup, never client decisions. */
export const FLIP_TILES_SIDE = 5;
export const FLIP_TILES_COUNT = FLIP_TILES_SIDE * FLIP_TILES_SIDE;

/** Authored per floor; the discriminator identifies the mechanic, not an instance. */
export type PuzzleSetup = { kind: "flip-tiles"; forceDamage: number } | { kind: "icy-pressure-pads" }
  | { kind: "chasm-crossing"; shimmerIntervalTicks: number; shimmerDurationTicks: number }
  | { kind: "orb-wizard"; bossId: number };
/** The smallest room with enough room for four pads and useful slide lanes. */
export const ICE_MIN_ROOM_SIDE = 5;
export interface PuzzlePlacement {
  /** One independent roll per eligible room. Missing = off. */
  puzzleRoomPct?: number;
  /** Uniform choice among enabled mechanics after a successful room roll. */
  puzzles?: readonly PuzzleSetup[];
}

export function readPuzzleRoomPct(value: unknown): number {
  if (value === undefined) return 0;
  if (!Number.isInteger(value) || typeof value !== "number" || value < 0 || value > 100) throw new Error("puzzleRoomPct must be an integer 0–100");
  return value;
}

/** Shared by store save, generated catalog validation and generation. Copies inputs. */
export function validatePuzzleSetups(value: unknown): PuzzleSetup[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) throw new Error("puzzles must be an array");
  const result: PuzzleSetup[] = [];
  for (const entry of value as unknown[]) {
    if (!entry || typeof entry !== "object") throw new Error("puzzles: each entry must be an object");
    const row = entry as Record<string, unknown>;
    if (row.kind !== "flip-tiles" && row.kind !== "icy-pressure-pads" && row.kind !== "chasm-crossing" && row.kind !== "orb-wizard") throw new Error(`puzzles: unknown kind ${String(row.kind)}`);
    if (result.some((p) => p.kind === row.kind)) throw new Error(`puzzles: duplicate kind ${row.kind}`);
    if (row.kind === "flip-tiles") {
      if (typeof row.forceDamage !== "number" || !Number.isSafeInteger(row.forceDamage) || row.forceDamage < 1 || row.forceDamage > 1000) throw new Error("puzzles: Flip tiles Force cost must be 1–1000 HP");
      result.push({ kind: row.kind, forceDamage: row.forceDamage });
    } else if (row.kind === "chasm-crossing") {
      const interval = row.shimmerIntervalTicks, duration = row.shimmerDurationTicks;
      if (typeof interval !== "number" || !Number.isSafeInteger(interval) || interval < 1 || interval > 12000) throw new Error("puzzles: shimmer interval must be 1–12000 ticks");
      if (typeof duration !== "number" || !Number.isSafeInteger(duration) || duration < 1 || duration > interval) throw new Error("puzzles: shimmer duration must be 1–interval ticks");
      result.push({ kind: row.kind, shimmerIntervalTicks: interval, shimmerDurationTicks: duration });
    } else if (row.kind === "orb-wizard") {
      if (typeof row.bossId !== "number" || !Number.isSafeInteger(row.bossId) || row.bossId < 0) throw new Error("puzzles: wizard boss ID must be a non-negative integer");
      result.push({ kind: row.kind, bossId: row.bossId });
    } else result.push({ kind: row.kind });
  }
  return result;
}

export interface FlipTilesPuzzle {
  id: number;
  kind: "flip-tiles";
  /** Top-left tile of the board, in world coordinates. */
  x: number;
  y: number;
  colours: number[];
  forceDamage: number;
  solved: boolean;
}

export interface IcyPressurePadsPuzzle {
  id: number;
  kind: "icy-pressure-pads";
  /** Top-left tile of the room-wide field; local indices use side. */
  x: number;
  y: number;
  side: number;
  /** One normal tile just inside each room door; local indices use side. */
  safeTiles: number[];
  obstacles: number[];
  pads: { index: number; active: boolean }[];
  solved: boolean;
}
/** Public knowledge only. The route is server-owned and is never a wire field. */
export interface ChasmPuzzle {
  id: number;
  kind: "chasm-crossing";
  x: number;
  y: number;
  side: number;
  normalTiles: number[];
  /** 0 unknown, 1 revealed chasm, 2 revealed safe. */
  tiles: number[];
  exits: number[];
  shimmerIntervalTicks: number;
  shimmerDurationTicks: number;
  shimmer: { axis: "row" | "column"; line: number; safeTiles: number[]; until: number } | null;
  solved: boolean;
}
export interface OrbWizardPuzzle {
  id: number;
  kind: "orb-wizard";
  x: number;
  y: number;
  side: number;
  bossId: number;
  enemyId: number | null;
  solved: boolean;
}
export interface WizardOrbWave {
  wave: number;
  /** No future target positions exist during the countdown. */
  startsAt: number;
  expiresAt: number;
  clicked: number;
  orbs: { id: number; x: number; y: number }[];
}
export type PuzzleState = FlipTilesPuzzle | IcyPressurePadsPuzzle | ChasmPuzzle | OrbWizardPuzzle;

/** Explicit copies at both the dungeon and observation boundaries. */
export function copyPuzzle(p: PuzzleState): PuzzleState {
  if (p.kind === "flip-tiles") return { ...p, colours: [...p.colours] };
  if (p.kind === "icy-pressure-pads") return { ...p, safeTiles: [...p.safeTiles], obstacles: [...p.obstacles], pads: p.pads.map((pad) => ({ ...pad })) };
  if (p.kind === "orb-wizard") return { ...p };
  // Explicit fields: a simulation instance also owns a secret route. Never spread it.
  return { id: p.id, kind: p.kind, x: p.x, y: p.y, side: p.side, normalTiles: [...p.normalTiles], tiles: [...p.tiles], exits: [...p.exits],
    shimmerIntervalTicks: p.shimmerIntervalTicks, shimmerDurationTicks: p.shimmerDurationTicks,
    shimmer: p.shimmer ? { ...p.shimmer, safeTiles: [...p.shimmer.safeTiles] } : null, solved: p.solved };
}

export function flipTileTarget(puzzleId: number, index: number): number {
  return puzzleId * FLIP_TILES_COUNT + index;
}

/** Self + orthogonal neighbours, clipped to the board rather than flat-array wrapping. */
export function flipTileIndices(index: number, force = false): number[] {
  if (!Number.isInteger(index) || index < 0 || index >= FLIP_TILES_COUNT) return [];
  const out = [index];
  if (force) return out;
  const x = index % FLIP_TILES_SIDE;
  const y = Math.floor(index / FLIP_TILES_SIDE);
  if (y > 0) out.push(index - FLIP_TILES_SIDE);
  if (x < FLIP_TILES_SIDE - 1) out.push(index + 1);
  if (y < FLIP_TILES_SIDE - 1) out.push(index + FLIP_TILES_SIDE);
  if (x > 0) out.push(index - 1);
  return out;
}
